import { createRouter, createWebHistory } from 'vue-router'
import { PERMISSIONS } from '@/config/permissions'
import HomeView from '@/views/HomeView.vue'
import NotFoundView from '@/views/NotFoundView.vue'
import {
  FlagIcon,
  BuildingStorefrontIcon,
  BuildingOffice2Icon,
  DocumentArrowUpIcon,
  DocumentTextIcon,
  HomeIcon,
  ReceiptPercentIcon,
  QueueListIcon,
  Cog6ToothIcon,
  BellIcon,
  UsersIcon,
  UserPlusIcon,
  KeyIcon,
  PuzzlePieceIcon,
} from '@heroicons/vue/24/outline/index.js'
import FilingCountryManagement from '@/views/FilingCountryManagement.vue'
import UnifiedMerchantManagementView from '@/views/UnifiedMerchantManagementView.vue'
import TaxInjectorView from '@/views/TaxInjectorView.vue'
import FraudCommandCenterView from '@/views/fcc/FraudCommandCenterView.vue'
import AlertsView from '@/views/fcc/AlertsView.vue'
import MerchantsView from '@/views/fcc/MerchantsView.vue'
import MerchantDetailsView from '@/views/fcc/MerchantDetailsView.vue'
import CasesView from '@/views/fcc/CasesView.vue'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: HomeView,
      meta: {
        displayName: 'Home',
        pageDescription: 'Welcome!',
        icon: HomeIcon,
        section: 'home',
        showInSidebar: true,
      },
    },
    {
      path: '/merchants',
      name: 'merchants',
      redirect: '/merchants/management',
      meta: {
        displayName: 'Merchants',
        pageDescription: 'Merchant management section',
        section: 'merchants',
        showInSidebar: false,
      },
      children: [
        {
          path: 'management',
          name: 'merchantManagement',
          component: UnifiedMerchantManagementView,
          meta: {
            displayName: 'Merchant Management',
            pageDescription: 'View and manage merchant configurations',
            perm: PERMISSIONS.TAX_CONFIG_READ,
            icon: BuildingStorefrontIcon,
            section: 'merchants',
            showInSidebar: true,
          },
          children: [
            {
              path: 'payouts/pending-assignment',
              name: 'merchantManagementPayoutsPending',
              component: UnifiedMerchantManagementView,
              meta: {
                displayName: 'Payouts Pending Assignment',
                pageDescription:
                  'Manage payouts that need bank account assignment',
                perm: PERMISSIONS.SHOPIFY_PAYMENTS_READ,
                section: 'merchants',
                showInSidebar: false,
                isDrawer: true,
              },
            },
            {
              path: ':id',
              name: 'merchantConfiguration',
              redirect: to => ({
                name: 'merchantConfigurationGeneral',
                params: { id: to.params.id },
              }),
              meta: {
                displayName: 'Configure',
                pageDescription: 'Configure merchant settings and integrations',
                perm: PERMISSIONS.TAX_CONFIG_READ,
                icon: Cog6ToothIcon,
                section: 'merchants',
                showInSidebar: false,
              },
              children: [
                {
                  path: '',
                  name: 'merchantConfigurationGeneral',
                  component: () =>
                    import(
                      '@/components/merchantmanagement/MerchantConfiguration.vue'
                    ),
                  meta: {
                    displayName: 'General Info',
                    pageDescription:
                      'Configure merchant settings and integrations',
                    perm: PERMISSIONS.TAX_CONFIG_READ,
                    section: 'merchants',
                    showInSidebar: true,
                  },
                },
                {
                  path: 'drop-in',
                  name: 'merchantConfigurationDropIn',
                  component: () =>
                    import(
                      '@/components/merchantmanagement/MerchantConfiguration.vue'
                    ),
                  meta: {
                    displayName: 'Drop-In',
                    pageDescription:
                      "The merchant's Drop-In webhook and integrator link",
                    perm: PERMISSIONS.DROPIN_MERCHANT_READ,
                    section: 'merchants',
                    showInSidebar: true,
                  },
                },
                {
                  path: 'shopify-stores/:storeId?',
                  name: 'merchantConfigurationShopifyStores',
                  component: () =>
                    import(
                      '@/components/merchantmanagement/MerchantConfiguration.vue'
                    ),
                  meta: {
                    displayName: 'Shopify Stores',
                    pageDescription: 'Configure Shopify stores',
                    perm: PERMISSIONS.TAX_CONFIG_READ,
                    section: 'merchants',
                    showInSidebar: true,
                  },
                  children: [
                    {
                      path: 'payouts',
                      name: 'shopifyStoresPayouts',
                      component: () =>
                        import(
                          '@/components/merchantmanagement/MerchantConfiguration.vue'
                        ),
                      meta: {
                        displayName: 'Shopify Payouts',
                        pageDescription: 'View Shopify Payouts',
                        perm: PERMISSIONS.TAX_CONFIG_READ,
                        section: 'merchants',
                        showInSidebar: false,
                        isDrawer: true,
                      },
                      children: [
                        {
                          // Legacy payout deep links predate the required
                          // business-entity segment. They can't name the owning
                          // entity, so they can't resolve a payout — bounce them
                          // to the payouts list instead of dead-ending on a
                          // no-match. `(\\d+)` matches numeric ids only, so it
                          // never shadows the `business-entity/...` detail route
                          // below.
                          path: ':payoutId(\\d+)',
                          redirect: to => ({
                            name: 'shopifyStoresPayouts',
                            params: {
                              id: to.params.id,
                              storeId: to.params.storeId,
                            },
                          }),
                        },
                        {
                          // The owning business entity is part of the payout's
                          // identity (the API path is
                          // `.../business-entity/{id}/payout/{id}`), so it's a
                          // required path segment on the detail route — the
                          // payout resolves in a single call. The payouts list
                          // above stays entity-agnostic.
                          path: 'business-entity/:businessEntityId/:payoutId',
                          name: 'shopifyStoresPayoutDetail',
                          component: () =>
                            import(
                              '@/components/merchantmanagement/MerchantConfiguration.vue'
                            ),
                          meta: {
                            displayName: 'Payout Detail',
                            pageDescription: 'View Payout Details',
                            perm: PERMISSIONS.TAX_CONFIG_READ,
                            section: 'merchants',
                            showInSidebar: false,
                            isDrawer: true,
                          },
                          children: [
                            {
                              path: 'transaction/:transactionId',
                              name: 'shopifyStoresBalanceTransactionDetail',
                              component: () =>
                                import(
                                  '@/components/merchantmanagement/MerchantConfiguration.vue'
                                ),
                              meta: {
                                displayName: 'Balance Transaction Detail',
                                pageDescription:
                                  'View Balance Transaction Details',
                                perm: PERMISSIONS.TAX_CONFIG_READ,
                                section: 'merchants',
                                showInSidebar: false,
                                isDrawer: true,
                              },
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
                {
                  path: 'tax-config',
                  name: 'merchantConfigurationTaxConfig',
                  component: () =>
                    import(
                      '@/components/merchantmanagement/MerchantConfiguration.vue'
                    ),
                  meta: {
                    displayName: 'Tax Configuration',
                    pageDescription: 'Configure tax settings',
                    perm: PERMISSIONS.TAX_CONFIG_READ,
                    section: 'merchants',
                    showInSidebar: true,
                  },
                },
                {
                  path: 'transaction-search',
                  name: 'merchantConfigurationTransactionSearch',
                  component: () =>
                    import(
                      '@/components/merchantmanagement/MerchantConfiguration.vue'
                    ),
                  meta: {
                    displayName: 'Transaction Search',
                    pageDescription: 'Search transactions for this merchant',
                    perm: PERMISSIONS.TAX_TRANSACTION_READ,
                    section: 'merchants',
                    showInSidebar: true,
                  },
                  children: [
                    {
                      path: ':transactionId',
                      name: 'merchantTransactionSearchDetail',
                      component: () =>
                        import(
                          '@/components/merchantmanagement/MerchantConfiguration.vue'
                        ),
                      meta: {
                        displayName: 'Transaction Details',
                        pageDescription: 'Transaction details',
                        perm: PERMISSIONS.TAX_TRANSACTION_READ,
                        section: 'merchants',
                        showInSidebar: false,
                        isDrawer: true,
                      },
                    },
                  ],
                },
                {
                  path: 'transaction-details',
                  name: 'merchantConfigurationTransactionDetails',
                  component: () =>
                    import(
                      '@/components/merchantmanagement/MerchantConfiguration.vue'
                    ),
                  meta: {
                    displayName: 'Transaction Details',
                    pageDescription:
                      'Review transaction-detail records for this merchant',
                    perm: PERMISSIONS.TRANSACTION_DETAILS_READ,
                    section: 'merchants',
                    showInSidebar: true,
                  },
                  children: [
                    {
                      // No UUID constraint on the param: a malformed id should
                      // reach the drawer's not-found state (the API answers 400)
                      // rather than falling through to a blank no-match page.
                      path: ':externalId',
                      name: 'merchantTransactionDetailRecord',
                      component: () =>
                        import(
                          '@/components/merchantmanagement/MerchantConfiguration.vue'
                        ),
                      meta: {
                        displayName: 'Transaction Detail Record',
                        pageDescription: 'Transaction detail record',
                        perm: PERMISSIONS.TRANSACTION_DETAILS_READ,
                        section: 'merchants',
                        showInSidebar: false,
                        isDrawer: true,
                      },
                    },
                  ],
                },
              ],
            },
            {
              path: ':id/configure/:pathMatch(.*)*',
              redirect: to => {
                const pathMatch = Array.isArray(to.params.pathMatch)
                  ? to.params.pathMatch
                  : []
                const base = `/merchants/management/${to.params.id}`
                return {
                  path:
                    pathMatch.length > 0
                      ? `${base}/${pathMatch.join('/')}`
                      : base,
                  query: to.query,
                  hash: to.hash,
                }
              },
            },
          ],
        },
        {
          path: 'integrators',
          name: 'dropInIntegrators',
          component: () => import('@/views/dropin/DropInIntegratorsView.vue'),
          meta: {
            displayName: 'Integrators',
            pageDescription: 'Drop-In integrators and their credentials',
            perm: PERMISSIONS.DROPIN_INTEGRATOR_READ,
            icon: PuzzlePieceIcon,
            section: 'merchants',
            showInSidebar: true,
          },
          // Nested so the breadcrumb (built from route.matched) links back to
          // the integrator list.
          children: [
            {
              path: ':integratorId',
              name: 'dropInIntegratorDetail',
              component: () =>
                import('@/views/dropin/DropInIntegratorDetailView.vue'),
              meta: {
                displayName: 'Integrator Details',
                pageDescription:
                  "An integrator's status, merchants and credentials",
                perm: PERMISSIONS.DROPIN_INTEGRATOR_READ,
                section: 'merchants',
                showInSidebar: false,
              },
            },
          ],
        },
      ],
    },
    {
      path: '/tax',
      name: 'tax',
      redirect: '/tax/filing-countries',
      meta: {
        // Label matches the sidebar "TAX" section header. Non-clickable in the
        // breadcrumb: there is no Tax landing page, only a redirect to a child.
        displayName: 'Tax',
        pageDescription: 'Tax management section',
        icon: ReceiptPercentIcon,
        section: 'tax',
        disableBreadcrumbLink: true,
      },
      children: [
        {
          path: 'filing-countries',
          name: 'filingCountryManagement',
          component: FilingCountryManagement,
          meta: {
            displayName: 'Filing Country Management',
            pageDescription: 'View and manage filing country configurations',
            perm: PERMISSIONS.TAX_CONFIG_READ,
            icon: FlagIcon,
            section: 'tax',
            showInSidebar: true,
          },
        },
        {
          path: 'filing-countries/:countryCode/configure',
          name: 'filingCountryConfiguration',
          component: () =>
            import('@/components/filingcountry/FilingCountryConfiguration.vue'),
          meta: {
            displayName: 'Configure Country',
            pageDescription:
              'Configure filing country settings and de minimis groups',
            perm: PERMISSIONS.TAX_CONFIG_READ,
            icon: Cog6ToothIcon,
            section: 'tax',
            showInSidebar: false,
          },
        },

        {
          path: 'injector/:externalId?',
          name: 'taxInjector',
          component: TaxInjectorView,
          meta: {
            displayName: 'Tax Injector',
            pageDescription:
              'Upload missed tax transactions or create new tax transactions to make corrections.',
            perm: PERMISSIONS.TAX_INJECTION_CREATE,
            icon: DocumentArrowUpIcon,
            section: 'tax',
            showInSidebar: true,
          },
        },
        {
          path: 'avalara-companies',
          name: 'avalaraCompanies',
          component: () => import('@/views/AvalaraCompaniesView.vue'),
          meta: {
            displayName: 'Avalara Companies',
            pageDescription: 'View Avalara companies',
            perm: PERMISSIONS.TAX_CONFIG_READ,
            icon: BuildingOffice2Icon,
            section: 'tax',
            showInSidebar: true,
          },
          children: [
            {
              path: 'companies/new',
              name: 'avalaraCompanyCreate',
              component: () => import('@/views/avalara/CreateAvalaraCompanyView.vue'),
              meta: {
                displayName: 'Create Company',
                pageDescription: 'Create a new Avalara company and apply a template',
                perm: PERMISSIONS.TAX_ADMIN_UPDATE,
                section: 'tax',
                showInSidebar: false,
              },
            },
            {
              path: ':companyId',
              name: 'avalaraCompanyDetail',
              component: () => import('@/views/AvalaraCompanyDetailView.vue'),
              meta: {
                displayName: 'Company Details',
                pageDescription: 'View Avalara company details',
                perm: PERMISSIONS.TAX_CONFIG_READ,
                section: 'tax',
                showInSidebar: false,
              },
            },
          ],
        },
      ],
    },
    {
      path: '/fraud',
      name: 'fraud',
      component: FraudCommandCenterView,
      redirect: '/fraud/alerts',
      meta: {
        perm: PERMISSIONS.FCC_READ,
        displayName: 'Fraud Command Center',
        pageDescription: 'Fraud tools',
        icon: DocumentTextIcon,
        section: 'fraud',
      },
      children: [
        {
          path: 'alerts',
          name: 'fccAlerts',
          component: AlertsView,
          meta: {
            displayName: 'Alerts Feed',
            pageDescription: 'FCC Alerts',
            perm: PERMISSIONS.FCC_READ,
            icon: BellIcon,
            section: 'fraud',
            showInSidebar: true,
          },
        },
        {
          path: 'merchants',
          name: 'fccMerchants',
          component: MerchantsView,
          meta: {
            displayName: 'Suppliers List',
            pageDescription: 'FCC Suppliers',
            perm: PERMISSIONS.FCC_READ,
            icon: UsersIcon,
            section: 'fraud',
            showInSidebar: true,
          },
        },
        {
          path: 'merchants/:id',
          name: 'fccMerchantDetails',
          component: MerchantDetailsView,
          meta: {
            displayName: 'Suppliers Details',
            pageDescription: 'FCC Suppliers Details',
            perm: PERMISSIONS.FCC_READ,
            icon: DocumentTextIcon,
            section: 'fraud',
            showInSidebar: false,
          },
        },
        {
          path: 'cases',
          name: 'fccCases',
          component: CasesView,
          meta: {
            displayName: 'Cases',
            pageDescription: 'FCC Cases',
            perm: PERMISSIONS.FCC_UPDATE,
            icon: QueueListIcon,
            section: 'fraud',
            showInSidebar: true,
          },
          children: [
            {
              path: ':id',
              name: 'fccCaseDetails',
              component: () =>
                import('@/components/fcc/cases/CaseDetailsModal.vue'),
              meta: {
                displayName: 'Case Details',
                pageDescription: 'FCC Case Details',
                perm: PERMISSIONS.FCC_UPDATE,
                icon: DocumentTextIcon,
                section: 'fraud',
                showInSidebar: false,
                isModal: true,
              },
            },
          ],
        },
      ],
    },
    {
      path: '/accounts',
      name: 'accounts',
      redirect: '/accounts/onboarding',
      meta: {
        // Clickable in the breadcrumb (unlike the Tax parent): the redirect
        // lands on the onboarding list, so "Accounts" is a usable way back
        // from the supplier detail page.
        displayName: 'Accounts',
        pageDescription: 'Account management section',
        icon: UserPlusIcon,
        section: 'accounts',
      },
      children: [
        {
          path: 'onboarding',
          name: 'accountOnboarding',
          component: () => import('@/views/AccountOnboardingView.vue'),
          meta: {
            displayName: 'Account Onboarding',
            pageDescription:
              'Initiate supplier onboarding and view onboarded accounts',
            perm: PERMISSIONS.ACCT_ONBOARDING_READ,
            icon: UserPlusIcon,
            section: 'accounts',
            showInSidebar: true,
          },
        },
        {
          path: 'onboarding/:externalId',
          name: 'accountOnboardingDetails',
          component: () => import('@/views/AccountOnboardingDetailsView.vue'),
          meta: {
            displayName: 'Supplier Details',
            pageDescription: 'Full record for an onboarded supplier account',
            perm: PERMISSIONS.ACCT_ONBOARDING_READ,
            icon: DocumentTextIcon,
            section: 'accounts',
            showInSidebar: false,
          },
        },
        {
          path: 'partners',
          name: 'partnerManagement',
          component: () => import('@/views/PartnersView.vue'),
          meta: {
            displayName: 'Partner Management',
            pageDescription: 'Create, list, and manage API partners',
            perm: PERMISSIONS.ACCT_ONBOARDING_READ,
            icon: KeyIcon,
            section: 'accounts',
            showInSidebar: true,
          },
          // Nested so the breadcrumb (built from route.matched) shows
          // "Partner Management" as a clickable ancestor on the detail page.
          children: [
            {
              path: ':externalId',
              name: 'partnerDetail',
              component: () => import('@/views/PartnerDetailView.vue'),
              meta: {
                displayName: 'Partner Details',
                pageDescription: 'View and manage a partner and its API tokens',
                perm: PERMISSIONS.ACCT_ONBOARDING_READ,
                icon: KeyIcon,
                section: 'accounts',
                showInSidebar: false,
              },
            },
          ],
        },
      ],
    },
    {
      path: '/:catchAll(.*)*',
      name: 'NotFound',
      component: NotFoundView,
    },
  ],
})

export default router
